<template>
  <transition name="fade">
    <Editor
        id="cardDesigner"
        v-if="showEditor"
        ref="editor"
        :editorMode="editorMode"
        :metadata="editorMetadata"
        :confetti="editorConfetti"
        :designId="editorDesignId"
    />
  </transition>
</template>

<script>
import Editor from "@/components/editor/Editor.vue";
import { trackEvent, dispatchHostEvent } from "@/utils/tracking";

export default {
  name: "App",
  components: {
    Editor,
  },
  data() {
    // Check if we're in library mode
    const isLibraryMode = window.__designerConfig?.isLibraryMode || false;
    const editorMode = window.__designerConfig?.editorMode || 'save';

    return {
      showEditor: isLibraryMode ? false : (import.meta.env.DEV || import.meta.env.VITE_APP_STANDALONE === 'true'),
      editorMode: editorMode, // 'save' or 'addToCart' - set from init()
      editorMetadata: null, // Metadata object with productId, variantId
      editorConfetti: true, // Default confetti enabled
      editorDesignId: null, // Design ID to load
    };
  },
  methods: {
    showEditorHandler(config = {}) {
      // Use mode from call parameter if provided, otherwise keep the one from init()
      if (config.mode) {
        this.editorMode = config.mode;
      }

      // Store metadata, confetti settings, and designId
      this.editorMetadata = config.meta || null;
      this.editorConfetti = config.confetti === true; // Default to false
      this.editorDesignId = config.designId || null;

      this.showEditor = true;

      trackEvent({
        eventName: "Customizer",
        eventType: "View",
        action: "EditorOpened",
        details: this.editorMode ? { mode: this.editorMode } : undefined,
      });

      // Set initialized flag in Editor component after it's mounted
      this.$nextTick(() => {
        if (this.$refs.editor) {
          this.$refs.editor.initialized = true;
        }
      });

      document.body.style.overflow = "hidden";
      const viewportMetaTag = document.querySelector('meta[name="viewport"]');
      if (viewportMetaTag) {
        viewportMetaTag.setAttribute(
            "content",
            "width=device-width, user-scalable=no"
        );
      }
    },
    hideEditorHandler() {
      // Reset initialized flag before hiding
      if (this.$refs.editor) {
        this.$refs.editor.initialized = false;
      }

      this.showEditor = false;
      document.body.style.overflow = "auto";
      const viewportMetaTag = document.querySelector('meta[name="viewport"]');
      if (viewportMetaTag) {
        viewportMetaTag.setAttribute(
            "content",
            "width=device-width,initial-scale=1.0"
        );
      }
    },
  },
  mounted() {

    // Expose methods globally for external access
    window.showEditor = this.showEditorHandler;
    window.hideEditor = this.hideEditorHandler;

    // Dispatch initialization event
    dispatchHostEvent("hwCustomizerInitialized");

    // Auto-show editor in development or standalone mode
    // BUT NOT if we're in library mode (need explicit showEditor() call)
    const isLibraryMode = window.__designerConfig?.isLibraryMode || false;
    if (!isLibraryMode && (import.meta.env.DEV || import.meta.env.VITE_APP_STANDALONE === 'true')) {
      window.showEditor();
    }

    // Handle language from query params
    const queryParams = new URLSearchParams(window.location.search);
    const lang = queryParams.get('lang');
    if (lang && this.$i18n.availableLocales.includes(lang)) {
      this.$i18n.locale = lang;
    }
  },
};
</script>

<style lang="scss">
#cardDesigner {

  background-color: var(--color-sol-black) !important;
  padding-bottom: env(safe-area-inset-bottom);

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
